import { useEffect, useMemo, useState } from 'react'
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Avatar, Alert, OutlinedInput, Checkbox, ListItemText,
} from '@mui/material'
import { Add, Edit } from '@mui/icons-material'
import { api } from '../../services/api'
import {
  ROLE_ORDER, displayNameOf, initialsOf, roleAssignments, roleChips, defaultPlantOf, sortUsers,
} from './users'

function roleColor(role) {
  const map = { admin: 'error', planner: 'primary', approver: 'warning', viewer: 'default' }
  return map[String(role).toLowerCase()] || 'default'
}

const formatDate = (iso) => {
  if (!iso) return 'Never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// FAR-85: Admin → Users on the real user records (models.User). Before, it
// read demo-only fields (name, string roles, FBO/Tucson) and crashed on the
// real data. No demo users: an empty list says so.
export default function UserManagement({ users: usersProp, onCreateUser, onUpdateUser }) {
  const users = useMemo(() => sortUsers(Array.isArray(usersProp) ? usersProp : []), [usersProp])
  const [plants, setPlants] = useState([]) // [{ code, name }]
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState(null)
  const [form, setForm] = useState({ displayName: '', email: '', roles: [], plant: '' })
  const [saveError, setSaveError] = useState(null)

  // Real plant codes and names, not a hardcoded FBO/Tucson list.
  useEffect(() => {
    let cancelled = false
    api.get('/plants/reference')
      .then((d) => { if (!cancelled) setPlants((d?.locations || []).filter((l) => l?.code)) })
      .catch(() => { if (!cancelled) setPlants([]) })
    return () => { cancelled = true }
  }, [])
  const plantName = (code) => plants.find((p) => p.code === code)?.name || code

  const openAdd = () => {
    setEditUser(null)
    setForm({ displayName: '', email: '', roles: [], plant: plants.find((p) => p.code === '1002')?.code || plants[0]?.code || '' })
    setSaveError(null)
    setDialogOpen(true)
  }

  const openEdit = (user) => {
    const assignments = roleAssignments(user)
    setEditUser(user)
    setForm({
      displayName: user.displayName || '',
      email: user.email || '',
      roles: [...new Set(assignments.map((r) => r.role))],
      plant: defaultPlantOf(user) || assignments[0]?.plant || '',
    })
    setSaveError(null)
    setDialogOpen(true)
  }

  // The record as the API stores it: roles at the chosen plant. Other fields
  // the form doesn't edit are kept as they were.
  const toRecord = () => ({
    ...(editUser || {}),
    displayName: form.displayName.trim(),
    email: form.email.trim(),
    roles: form.roles.map((role) => ({ role, plant: form.plant })),
    defaultPlant: form.plant,
  })

  const save = async () => {
    setSaveError(null)
    try {
      if (editUser) await onUpdateUser?.(editUser.id, toRecord())
      else await onCreateUser?.(toRecord())
      setDialogOpen(false)
    } catch (err) {
      setSaveError(err?.message || 'Could not save the user')
    }
  }

  const valid = form.displayName.trim() && form.email.trim() && form.roles.length > 0 && form.plant

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary" data-testid="users-count">
          {users.length} user{users.length === 1 ? '' : 's'} configured
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add User</Button>
      </Box>

      <Card variant="outlined">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Roles</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Default Plant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>No users</Typography>
                  </TableCell>
                </TableRow>
              )}
              {users.map((user) => (
                <TableRow key={user.id || user.email} hover data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'primary.main' }}>{initialsOf(user)}</Avatar>
                      <Typography variant="body2">{displayNameOf(user)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{user.email || '—'}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {roleChips(user, plantName).map((c) => (
                        <Chip key={c.key} label={c.label} size="small" color={roleColor(c.role)} variant="outlined" />
                      ))}
                      {roleAssignments(user).length === 0 && <Typography variant="caption" color="text.secondary">No roles</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>{defaultPlantOf(user) ? plantName(defaultPlantOf(user)) : '—'}</TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{formatDate(user.lastLogin)}</Typography></TableCell>
                  <TableCell>
                    <IconButton size="small" aria-label={`Edit ${displayNameOf(user)}`} onClick={() => openEdit(user)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField label="Name" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} fullWidth />
          <TextField label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel id="user-roles">Roles</InputLabel>
            <Select labelId="user-roles" multiple value={form.roles} input={<OutlinedInput label="Roles" />}
              onChange={(e) => setForm((f) => ({ ...f, roles: e.target.value }))} renderValue={(sel) => sel.join(', ')}>
              {ROLE_ORDER.map((r) => (
                <MenuItem key={r} value={r}><Checkbox checked={form.roles.includes(r)} /><ListItemText primary={r} /></MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="user-plant">Plant</InputLabel>
            <Select labelId="user-plant" label="Plant" value={form.plant} onChange={(e) => setForm((f) => ({ ...f, plant: e.target.value }))}>
              {plants.map((p) => <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">The roles apply at this plant.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!valid}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
